/**
 * Custom API Error Classes
 */

export class ApiError extends Error {
  status?: number;
  statusText?: string;
  data?: any;
  code?: string;

  constructor(
    message: string,
    status?: number,
    statusText?: string,
    data?: any,
    code?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.data = data;
    this.code = code;

    // Maintains proper stack trace for where our error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
  }

  static fromResponse(response: Response, data?: any): ApiError {
    const message = data?.message || `API error: ${response.status} ${response.statusText}`;
    return new ApiError(
      message,
      response.status,
      response.statusText,
      data,
      data?.code
    );
  }

  static fromNetworkError(error: Error): ApiError {
    // Check if it's a CORS error
    const isCorsError = 
      error.message.includes('CORS') ||
      error.message.includes('Access-Control') ||
      error.message.includes('Failed to fetch') ||
      error.message.includes('network error');
    
    const message = isCorsError
      ? 'CORS Error: The server is not allowing requests from this origin. Please check the server CORS configuration.'
      : error.message || 'Network error occurred';
    
    return new ApiError(
      message,
      undefined,
      undefined,
      undefined,
      isCorsError ? 'CORS_ERROR' : 'NETWORK_ERROR'
    );
  }

  static fromTimeout(): ApiError {
    return new ApiError(
      'Request timeout. Please try again.',
      undefined,
      undefined,
      undefined,
      'TIMEOUT'
    );
  }

  static fromAbort(): ApiError {
    return new ApiError(
      'Request was cancelled',
      undefined,
      undefined,
      undefined,
      'ABORTED'
    );
  }

  isNetworkError(): boolean {
    return this.code === 'NETWORK_ERROR' || this.code === 'CORS_ERROR';
  }

  isCorsError(): boolean {
    return this.code === 'CORS_ERROR';
  }

  isTimeout(): boolean {
    return this.code === 'TIMEOUT';
  }

  isAborted(): boolean {
    return this.code === 'ABORTED';
  }

  isClientError(): boolean {
    return this.status !== undefined && this.status >= 400 && this.status < 500;
  }

  isServerError(): boolean {
    return this.status !== undefined && this.status >= 500;
  }

  isUnauthorized(): boolean {
    return this.status === 401;
  }

  isForbidden(): boolean {
    return this.status === 403;
  }

  isNotFound(): boolean {
    return this.status === 404;
  }
}


